// @flow

import React, { useMemo, useRef } from "react";
import { localize } from "redux-i18n";

import type { AbsenceReviewAppeal } from "@modules/shared/reducer/absence-review/types";
import { Button, SchemaForm, View } from "@components";

import { getSchema } from "./schema";
import { getUiSchema } from "./ui-schema";

type Props = PropsFromLocalize & {
  submittedAppeal: ?AbsenceReviewAppeal,
  appealReasons: Array<Object>,
  clientId: number,
  employeeId: number,
  taskId: number,
  addAppeal: Function,
  updateAppeal: Function,
};

const AppealForm = (props: Props) => {
  const { t, submittedAppeal, appealReasons } = props;
  const formRef = useRef(null);

  const schema = getSchema({ t, appealReasons });
  const uiSchema = getUiSchema({ t, fileName: submittedAppeal?.appealForm?.fileName });

  const formData = useMemo(() => {
    if (submittedAppeal && submittedAppeal.id) {
      const matchedReason = appealReasons.find(r => r.name === submittedAppeal.reasonForAppeal);
      return {
        dateReceived: submittedAppeal.dateReceived,
        reasonForAppeal: matchedReason ? { id: matchedReason.id } : null,
        additionalNotes: submittedAppeal.additionalNotes,
        appealForm: submittedAppeal.appealForm?.url,
      };
    }
    return null;
  }, [submittedAppeal, appealReasons]);

  const handleSubmit = formData => {
    const { dateReceived, reasonForAppeal, additionalNotes, appealForm } = formData;
    const appealData = {
      dateReceived,
      reasonForAppeal: reasonForAppeal?.id
        ? appealReasons.find(r => r.id === reasonForAppeal.id)?.name
        : null,
      additionalNotes,
      appealForm,
    };

    if (submittedAppeal && submittedAppeal.id) {
      props.updateAppeal({
        clientId: props.clientId,
        employeeId: props.employeeId,
        taskId: props.taskId,
        appealId: submittedAppeal.id,
        appeal: {
          id: submittedAppeal.id,
          ...appealData,
        },
      });
    } else {
      props.addAppeal({
        clientId: props.clientId,
        employeeId: props.employeeId,
        taskId: props.taskId,
        appeal: appealData,
      });
    }
  };

  const submitForm = () => {
    formRef.current.submit();
  };

  const isDisabled = useMemo(() => {
    return (
      submittedAppeal?.status === "UPHELD" ||
      submittedAppeal?.status === "NOT_UPHELD" ||
      submittedAppeal?.status === "CANCELED"
    );
  }, [submittedAppeal?.status]);

  return (
    <View gap={4}>
      <SchemaForm
        ref={formRef}
        schema={schema}
        formData={formData}
        onSubmit={handleSubmit}
        uiSchema={uiSchema}
        disabled={isDisabled}
        externalSubmit
      />
      <View direction="row" justifyContent="end">
        <Button
          onClick={submitForm}
          materialIcon={submittedAppeal?.id ? "update" : "add_box"}
          disabled={isDisabled}
        >
          {t(submittedAppeal?.id ? "UPDATE" : "SUBMIT")}
        </Button>
      </View>
    </View>
  );
};

export default localize()(AppealForm);
